import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const OwnerOverview = ({ onNavigate, currentUser }) => {
    // --- GLOBAL DATA STATE ---
    const [stats, setStats] = useState({ logs: [], permits: [], safety: [] });
    const [loading, setLoading] = useState(true);
    
    // --- LOG FILTER STATE ---
    const [logTab, setLogTab] = useState('today'); // 'today', 'history'
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [filterMyLogs, setFilterMyLogs] = useState(false);
    const [historyFilter, setHistoryFilter] = useState({ company: '', date_from: '', date_to: '' });
    
    // --- UI STATE ---
    const [expandedSection, setExpandedSection] = useState('logs'); 
    const [selectedLog, setSelectedLog] = useState(null); 
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        loadData();
        return () => window.removeEventListener('resize', handleResize);
    }, [filterDate, logTab]);

    const loadData = async () => {
        try {
            let logUrl = 'logs/';
            if (logTab === 'today') logUrl += `?date=${filterDate}`;
            
            const [logRes, permRes, safeRes] = await Promise.all([
                api.get(logUrl),
                api.get('approvals/todo/'),
                api.get('safety/?status=OPEN')
            ]);

            setStats({
                logs: logRes.data,
                permits: permRes.data,
                safety: safeRes.data
            });
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    // --- LOG LOGIC ---
    const filteredLogs = useMemo(() => {
        let data = [...stats.logs];
        if (filterMyLogs && currentUser) {
            data = data.filter(l => (l.engineer_name || '').toLowerCase() === currentUser.toLowerCase());
        }
        if (logTab === 'history') {
            if (historyFilter.company) data = data.filter(l => l.company.toLowerCase().includes(historyFilter.company.toLowerCase()));
            if (historyFilter.date_from) data = data.filter(l => new Date(l.date) >= new Date(historyFilter.date_from));
            if (historyFilter.date_to) data = data.filter(l => new Date(l.date) <= new Date(historyFilter.date_to));
        }
        return data;
    }, [stats.logs, filterMyLogs, historyFilter, logTab, currentUser]);

    const logSummary = useMemo(() => {
        const headcount = filteredLogs.reduce((sum, l) => sum + (parseInt(l.headcount) || 0), 0);
        const hours = filteredLogs.reduce((sum, l) => sum + ((parseInt(l.headcount)||0) * (parseFloat(l.shift_hours)||0)), 0);
        return { headcount, hours, count: filteredLogs.length };
    }, [filteredLogs]);

    const handleExportLogs = () => {
        if (filteredLogs.length === 0) return alert("No logs to export");
        const headers = ["ID", "Ref #", "Date", "Company", "Project", "Location", "Engineer", "Headcount", "Shift(Hrs)", "Notes"];
        const csvRows = [headers.join(',')];
        
        filteredLogs.forEach(row => {
            const clean = (t) => `"${(t||'').toString().replace(/"/g, '""')}"`;
            csvRows.push([
                row.id, 
                clean(row.display_id), // NEW: Export the Scoped ID
                row.date, clean(row.company), clean(row.project_name), 
                clean(row.location_detail), clean(row.engineer_name), 
                row.headcount, row.shift_hours, clean(row.notes)
            ].join(','));
        });

        const url = window.URL.createObjectURL(new Blob([csvRows.join('\n')], { type: 'text/csv' }));
        const a = document.createElement('a'); a.href = url; a.download = `Site_Logs_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    // --- RENDER HELPERS ---
    const DashboardCard = ({ id, title, color, icon, summary, children }) => {
        const isExpanded = expandedSection === id;
        return (
            <div style={{marginBottom:'20px', background:THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, borderLeft:`5px solid ${color}`, overflow:'hidden'}}>
                <div onClick={() => setExpandedSection(isExpanded ? null : id)} style={{padding:'20px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', background: isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
                        <div style={{fontSize:'1.5rem'}}>{icon}</div>
                        <div>
                            <h3 style={{margin:0, color:'white', fontSize:'1.1rem'}}>{title}</h3>
                            <div style={{color:THEME.textSec, fontSize:'0.85rem', marginTop:'4px'}}>{summary}</div>
                        </div>
                    </div>
                    <div style={{color:THEME.textSec}}>{isExpanded ? '▼' : '▶'}</div>
                </div>
                {isExpanded && <div style={{borderTop:`1px solid ${THEME.border}`, padding:'20px', background:'#0f172a'}}>{children}</div>}
            </div>
        );
    };

    if (loading) return <div style={{color:'white'}}>Loading...</div>;

    return (
        <div className="animate-fade-in">
            {/* 1. DAILY ACTIVITY LOGS */}
            <DashboardCard 
                id="logs" 
                title="Daily Site Activity" 
                color={THEME.primary} 
                icon="📋"
                summary={`${logSummary.headcount} Workers • ${logSummary.hours.toFixed(1)} Man-Hours`}
            >
                {/* LOG TOOLBAR */}
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px', flexWrap:'wrap', gap:'10px'}}>
                    
                    {/* View Tabs */}
                    <div style={{display:'flex', gap:'5px', background:THEME.card, padding:'4px', borderRadius:'6px', border:`1px solid ${THEME.border}`}}>
                        <button onClick={() => setLogTab('today')} style={{padding:'6px 12px', borderRadius:'4px', border:'none', cursor:'pointer', background: logTab==='today'?THEME.primary:'transparent', color: logTab==='today'?'white':THEME.textSec}}>Today</button>
                        <button onClick={() => setLogTab('history')} style={{padding:'6px 12px', borderRadius:'4px', border:'none', cursor:'pointer', background: logTab==='history'?THEME.primary:'transparent', color: logTab==='history'?'white':THEME.textSec}}>History</button>
                    </div>
                    
                    {logTab === 'today' && (
                        <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap: 'wrap'}}>
                            <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{padding:'6px', borderRadius:'4px', border:`1px solid ${THEME.border}`, background:THEME.bg, color:'white'}} />
                            
                            <div 
                                onClick={() => setFilterMyLogs(!filterMyLogs)} 
                                style={{
                                    padding:'6px 12px', borderRadius:'20px', cursor:'pointer', fontSize:'0.85rem', fontWeight: 600, userSelect:'none', whiteSpace:'nowrap',
                                    border: filterMyLogs ? `1px solid ${THEME.primary}` : `1px solid ${THEME.border}`, 
                                    background: filterMyLogs ? 'rgba(59, 130, 246, 0.2)' : 'transparent', 
                                    color: filterMyLogs ? THEME.primary : THEME.textSec
                                }}
                            >
                                {filterMyLogs ? '✓ My Logs Only' : 'My Logs Only'}
                            </div>
                        </div>
                    )}
                    
                    {logTab === 'history' && (
                        <div style={{display:'flex', gap:'5px', flexWrap:'wrap'}}>
                            <input placeholder="Company..." value={historyFilter.company} onChange={e => setHistoryFilter({...historyFilter, company: e.target.value})} style={{padding:'6px', borderRadius:'4px', border:`1px solid ${THEME.border}`, background:THEME.bg, color:'white', width:'120px'}} />
                            <input type="date" value={historyFilter.date_from} onChange={e => setHistoryFilter({...historyFilter, date_from: e.target.value})} style={{padding:'6px', borderRadius:'4px', border:`1px solid ${THEME.border}`, background:THEME.bg, color:'white'}} />
                        </div>
                    )}

                    <button onClick={handleExportLogs} style={{background:THEME.success, color:'white', border:'none', padding:'6px 12px', borderRadius:'4px', cursor:'pointer', fontWeight:'bold'}}>📥 CSV</button>
                </div>

                {/* LOG TABLE */}
                <div style={{overflowX: 'auto'}}>
                    <table style={{width:'100%', minWidth:'700px', textAlign:'left', color:THEME.textSec, fontSize:'0.9rem', borderCollapse:'collapse'}}>
                        <thead>
                            <tr style={{borderBottom:`1px solid ${THEME.border}`, color:THEME.primary}}>
                                <th style={{padding:'10px'}}>Ref #</th> {/* Changed from Date to Ref if available, or just add ID col */}
                                <th style={{padding:'10px'}}>Date</th>
                                <th>Company</th>
                                <th>Location</th>
                                <th>Headcount</th>
                                <th>Shift</th>
                                <th>Permits</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLogs.map(l => (
                                <React.Fragment key={l.id}>
                                    <tr 
                                        onClick={() => setSelectedLog(selectedLog === l.id ? null : l.id)} 
                                        style={{borderBottom:`1px solid ${THEME.border}`, cursor:'pointer', background: selectedLog === l.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent'}}
                                    >
                                        <td style={{padding:'10px', color:THEME.primary, fontWeight:'bold'}}>{l.display_id || '#'+l.id}</td>
                                        <td style={{padding:'10px', color:'white'}}>{l.date}</td>
                                        <td style={{fontWeight:'bold', color: THEME.warning}}>{l.company}</td>
                                        <td>{l.location_detail}</td>
                                        <td style={{fontWeight:'bold', color:'white'}}>{l.headcount}</td>
                                        <td>{l.shift_hours}h</td>
                                        <td>
                                            {l.permits && l.permits.length > 0 ? 
                                                <span style={{background:THEME.card, padding:'2px 6px', borderRadius:'4px', border:`1px solid ${THEME.border}`, fontSize:'0.75rem'}}>{l.permits.length} Linked</span> 
                                                : '-'}
                                        </td>
                                    </tr>
                                    {selectedLog === l.id && (
                                        <tr style={{background:THEME.card, borderBottom:`2px solid ${THEME.primary}`}}>
                                            <td colSpan="7" style={{padding:'20px'}}>
                                                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap:'20px'}}>
                                                    <div>
                                                        <h4 style={{marginTop:0, color:'white'}}>📝 Work Description</h4>
                                                        <div style={{whiteSpace:'pre-wrap', color:THEME.textSec}}>{l.notes}</div>
                                                        
                                                        {/* LINKED PERMITS (UPDATED ID DISPLAY) */}
                                                        {l.permits && l.permits.length > 0 && (
                                                            <div style={{marginTop:'20px'}}>
                                                                <h4 style={{marginTop:0, color:'white'}}>📜 Linked Permits</h4>
                                                                <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>
                                                                    {l.permits.map(p => (
                                                                        <button 
                                                                            key={p.id}
                                                                            onClick={() => onNavigate('permits', p.id)}
                                                                            style={{
                                                                                padding:'6px 12px', 
                                                                                borderRadius:'6px', 
                                                                                border: `1px solid ${THEME.warning}`, 
                                                                                background: 'rgba(250, 204, 21, 0.1)', 
                                                                                color: THEME.warning, 
                                                                                cursor:'pointer', 
                                                                                fontSize:'0.85rem',
                                                                                fontWeight: 'bold',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '5px'
                                                                            }}
                                                                        >
                                                                            🔗 {p.permit_type_name} {p.display_id || `#${p.id}`} ({p.status})
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h4 style={{marginTop:0, color:'white'}}>👥 Register</h4>
                                                        {l.worker_manifest && l.worker_manifest.length > 0 ? (
                                                            <div style={{display:'flex', flexWrap:'wrap', gap:'5px'}}>
                                                                {l.worker_manifest.map((w, idx) => (
                                                                    <span key={idx} style={{background:'#0f172a', padding:'4px 8px', borderRadius:'12px', fontSize:'0.75rem', border:`1px solid ${THEME.border}`}}>{w.name}</span>
                                                                ))}
                                                            </div>
                                                        ) : <span style={{color:THEME.textSec, fontStyle:'italic'}}>No register data.</span>}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filteredLogs.length === 0 && <div style={{padding:'20px', textAlign:'center', color:THEME.textSec}}>No logs found.</div>}
            </DashboardCard>

            {/* 2. PERMITS CARD */}
            <DashboardCard 
                id="permits" 
                title="Permit Actions" 
                color={THEME.warning} 
                icon="⚠️"
                summary={`${stats.permits.length} Permits Pending Approval`}
            >
                {stats.permits.length === 0 ? <p style={{color:THEME.success}}>✅ All clear. No pending permits.</p> : (
                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                        {stats.permits.map(p => (
                            <div key={p.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', background:THEME.card, padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`}}>
                                <div>
                                    {/* UPDATED: Show Display ID */}
                                    <div style={{fontWeight:'bold', color:'white'}}>{p.permit_type_details?.name} {p.display_id || `#${p.id}`}</div>
                                    <div style={{fontSize:'0.85rem', color:THEME.textSec}}>{p.company_name} • {p.location_label}</div>
                                </div>
                                <button onClick={() => onNavigate('permits', p.id)} style={{padding:'6px 12px', background:THEME.warning, color:'black', border:'none', borderRadius:'4px', fontWeight:'bold', cursor:'pointer', fontSize:'0.8rem'}}>Review</button>
                            </div>
                        ))}
                    </div>
                )}
            </DashboardCard>

            {/* 3. SAFETY CARD */}
            <DashboardCard 
                id="safety" 
                title="Safety Incidents" 
                color={THEME.danger} 
                icon="🚨"
                summary={`${stats.safety.length} Open Reports`}
            >
                {stats.safety.length === 0 ? <p style={{color:THEME.success}}>✅ No open safety incidents.</p> : (
                    <div style={{overflowX: 'auto'}}>
                        <table style={{width:'100%', minWidth:'500px', textAlign:'left', color:THEME.textSec, fontSize:'0.9rem'}}>
                            <thead><tr style={{color:THEME.danger}}><th>ID</th><th>Type</th><th>Location</th><th>Severity</th><th>Action</th></tr></thead>
                            <tbody>
                                {stats.safety.map(r => (
                                    <tr key={r.id}>
                                        <td style={{padding:'8px 0', color:'white'}}>#{r.id}</td> {/* Safety IDs can also be scoped if needed later */}
                                        <td>{r.report_type}</td>
                                        <td>{r.location}</td>
                                        <td style={{color: r.severity==='CRITICAL'?THEME.danger:THEME.warning, fontWeight:'bold'}}>{r.severity}</td>
                                        <td>
                                            <button 
                                                onClick={() => onNavigate('safety', r.id)} 
                                                style={{background:'transparent', border:`1px solid ${THEME.danger}`, color:THEME.danger, borderRadius:'4px', padding:'2px 8px', cursor:'pointer', fontSize:'0.75rem'}}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </DashboardCard>
        </div>
    );
};

export default OwnerOverview;